import { bytesToHex, CanonicalEncoder, hexToBytes } from "./encoding";

export const BN254_SCALAR_MODULUS =
  0x30644e72e131a029b85045b68181585d2833e84879b9709143e1f593f0000001n;

let poseidonPromise: Promise<
  Awaited<ReturnType<(typeof import("circomlibjs"))["buildPoseidon"]>>
> | null = null;

const poseidon = () => {
  poseidonPromise ??= import("circomlibjs").then(({ buildPoseidon }) =>
    buildPoseidon()
  );
  return poseidonPromise;
};

export const bigintToFieldHex = (value: bigint): string => {
  if (value < 0n || value >= BN254_SCALAR_MODULUS) {
    throw new Error("Value is outside the BN254 scalar field");
  }
  return value.toString(16).padStart(64, "0");
};

export const parseField = (value: string): bigint => {
  hexToBytes(value, 32);
  const parsed = BigInt(`0x${value}`);
  if (parsed >= BN254_SCALAR_MODULUS) {
    throw new Error("Value is outside the BN254 scalar field");
  }
  return parsed;
};

export const sha256 = async (value: Uint8Array): Promise<Uint8Array> =>
  new Uint8Array(await crypto.subtle.digest("SHA-256", value));

export const hashToField = async (value: Uint8Array): Promise<string> => {
  const digest = await sha256(value);
  return bigintToFieldHex(
    BigInt(`0x${bytesToHex(digest)}`) % BN254_SCALAR_MODULUS
  );
};

export const poseidonHash = async (
  values: readonly string[]
): Promise<string> => {
  if (![2, 3, 6].includes(values.length)) {
    throw new Error("Unsupported Private Mode proof configuration");
  }
  const instance = await poseidon();
  const result = instance(values.map((value) => parseField(value).toString()));
  return bigintToFieldHex(BigInt(instance.F.toString(result)));
};

export const randomField = (): string => {
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const value = BigInt(`0x${bytesToHex(bytes)}`);
    if (value > 0n && value < BN254_SCALAR_MODULUS)
      return bigintToFieldHex(value);
  }
};

const domainField = (value: number): string => bigintToFieldHex(BigInt(value));

export const denomHash = async (denom: string): Promise<string> => {
  const encoded = new CanonicalEncoder()
    .pushBytes(new TextEncoder().encode("redacted-v2-denom"))
    .pushString(denom)
    .finish();
  return hashToField(encoded);
};

export const addressHash = async (address: string): Promise<string> => {
  const encoded = new CanonicalEncoder()
    .pushBytes(new TextEncoder().encode("redacted-v2-address"))
    .pushString(address)
    .finish();
  return hashToField(encoded);
};

export const reserveCommitment = async (value: {
  ownerKey: string;
  rho: string;
  denomHash: string;
  amount: bigint;
  blinding: string;
}): Promise<string> =>
  poseidonHash([
    domainField(101),
    value.ownerKey,
    value.rho,
    value.denomHash,
    bigintToFieldHex(value.amount),
    value.blinding,
  ]);

export const reserveNullifier = (
  ownerKey: string,
  rho: string
): Promise<string> => poseidonHash([domainField(102), ownerKey, rho]);

export const controlCommitment = async (value: {
  ownerKey: string;
  rho: string;
  subwalletHash: string;
  controlNonce: bigint;
  blinding: string;
}): Promise<string> =>
  poseidonHash([
    domainField(103),
    value.ownerKey,
    value.rho,
    value.subwalletHash,
    bigintToFieldHex(value.controlNonce),
    value.blinding,
  ]);

export const controlNullifier = (
  ownerKey: string,
  rho: string
): Promise<string> => poseidonHash([domainField(104), ownerKey, rho]);

export const merkleHash = (left: string, right: string): Promise<string> =>
  poseidonHash([left, right]);
